import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import { deepFreeze, timestampToDate } from "../util/misc";
import { cloneDeep, every, get, isEmpty, isNumber } from "lodash";
import { diff } from "deep-object-diff";
import { startOfToday } from "date-fns";

/**
 * An acknowledgement that is not for a sale.
 *
 * A vendor's acknowledgement confirms an order we placed. The ones for a
 * sale's order live under that order (Ack.js). These are the rest: sample
 * orders placed under a PO that marks them as samples, parts for a service
 * issue the manufacturer ordered itself, general expenses, stock. They had
 * nowhere to go (Justin, 2026-09-30: they "fall to the nether"). Filed here,
 * each says what the material is before it arrives, and receiving marks it
 * received.
 *
 * They are a collection of their own, `otherAcks`, and must stay one:
 * anything named `acks` is swept into the collection-group queries built for
 * sale acks, whose converter and the Marvin poller read the sale out of the
 * document's path.
 */

// What an acknowledgement is for. Type labels are Title Case, like other
// category labels; only status labels are sentence case.
export const OTHER_ACK_TYPES = {
  service: { label: "Service Issue Parts", icon: "service" },
  sample: { label: "Samples", icon: "sample" },
  expense: { label: "General Expense", icon: "expense" },
  stock: { label: "Stock", icon: "inventory" },
  other: { label: "Other", icon: "label" },
};
deepFreeze(OTHER_ACK_TYPES);

export const OTHER_ACK_STATUS_TYPES = {
  expected: { label: "Expected", icon: "pending", color: "info" },
  late: { label: "Past expected date", icon: "pending_date", color: "warning" },
  received: { label: "Received", icon: "done", color: "positive" },
};
deepFreeze(OTHER_ACK_STATUS_TYPES);

export const OTHER_ACK_FIELDS = {
  // What it is for (OTHER_ACK_TYPES), and what the material is
  type: "",
  description: "",
  // `service`: the issue the parts are for
  serviceIssueID: "",
  // `expense`: the expense category it would be filed under
  expenseCategory: "",

  // The order, as the acknowledgement states it
  vendor: "",
  orderPO: "", // The PO we gave the vendor, e.g. "TK-SAMPLES"
  vendorOrderNumber: "",
  expectedDate: null,
  totalCost: null,
  documents: [], // The acknowledgement copy
  notes: "",

  // Written when it arrives: by receiving, or Mark Received
  fullyReceived: false,
  receivedDate: null,
  receivedBy: "",
  receivedLocation: "",
  receivedSessionID: "",
  receivedTruckNumber: "",

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(OTHER_ACK_FIELDS);

export default class OtherAck extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, OTHER_ACK_FIELDS);

    // Convert to Date
    data.expectedDate = timestampToDate(data.expectedDate);
    data.receivedDate = timestampToDate(data.receivedDate);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });

    // Documents
    this.data.documents = this.documents.map((d) => d.data);
  }

  // Permissions

  static canBeCreatedBy(user) {
    return Boolean(user && user.isEmployee);
  }

  canBeEditedBy(user) {
    return Boolean(user && user.isEmployee);
  }

  // A received acknowledgement records a receipt, so only an admin removes
  // it; before that, whoever filed it can take it back.
  canBeDeletedBy(user) {
    return Boolean(
      user &&
        user.isEmployee &&
        (user.isAdmin ||
          (!this.data.fullyReceived && user.id === this.data.createdBy))
    );
  }

  // Database

  get path() {
    return OtherAck.getPath(this.id);
  }

  static getPath(id) {
    return id ? `otherAcks/${id}` : "otherAcks";
  }

  static toFirestore(ack) {
    return ack.data;
  }

  static fromFirestore(snapshot, options) {
    return new OtherAck(snapshot.id, snapshot.data(options));
  }

  // Editing

  get rules() {
    const filled = (a) => Boolean(a && String(a).trim().length);
    return {
      type: [(a) => (Boolean(a) && a in OTHER_ACK_TYPES) || "required"],
      description: [
        (a) => this.data.type !== "other" || filled(a) || "required",
      ],
      serviceIssueID: [
        (a) => this.data.type !== "service" || filled(a) || "required",
      ],
      expenseCategory: [
        (a) => this.data.type !== "expense" || filled(a) || "required",
      ],
      vendor: [(a) => filled(a) || "required"],
      // Receiving recognises the material by one or the other.
      orderPO: [
        (a) =>
          filled(a) || filled(this.data.vendorOrderNumber) || "otherAckNumber",
      ],
      vendorOrderNumber: [
        (a) => filled(a) || filled(this.data.orderPO) || "otherAckNumber",
      ],
      totalCost: [
        (a) => a === null || (isNumber(a) && a >= 0) || "totalCostInvalid",
      ],
      // The copy is optional: a manufacturer's order may come with only its
      // number. The empty row the list starts with is dropped on save; a row
      // with a file must have a usable one.
      documents: [
        () =>
          this.documents.every((d) => !d.data.file || d.isValid) ||
          "validDocumentRequired",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(diff(OtherAck.sanitize(cloneDeep(this.data)), OTHER_ACK_FIELDS))
    );
  }

  isEqual(ack) {
    return (
      this.id === ack.id &&
      isEmpty(
        diff(
          OtherAck.sanitize(cloneDeep(this.data)),
          OtherAck.sanitize(cloneDeep(ack.data))
        )
      )
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    data.documents = (data.documents || []).filter(
      (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
    );
    return data;
  }

  static sanitize(data, removeUnsaved) {
    OtherAck.cleanDocuments(data, removeUnsaved);
    // Only the field its type uses is kept, so a type changed while editing
    // does not leave the old type's link behind.
    if (data.type !== "service") data.serviceIssueID = "";
    if (data.type !== "expense") data.expenseCategory = "";
    return data;
  }

  // Formatting

  get typeInfo() {
    return OTHER_ACK_TYPES[this.data.type] || null;
  }

  get status() {
    if (this.data.fullyReceived) return "received";
    const expected = this.data.expectedDate;
    return expected && expected < startOfToday() ? "late" : "expected";
  }

  get statusType() {
    const type = OTHER_ACK_STATUS_TYPES[this.status];
    return { ...type, ...COLORS[type.color] };
  }

  get serviceIssue() {
    return this.data.serviceIssueID
      ? store.state.main.serviceIssues[this.data.serviceIssueID] || null
      : null;
  }

  get documents() {
    return (this.data.documents || []).map((d) => new DocumentRef(d));
  }

  /** The numbers it goes by: our PO, then the vendor's order number. */
  get reference() {
    return [this.data.orderPO, this.data.vendorOrderNumber]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" · ");
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  // The dialog header's info reads who made and last changed it.
  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  get receivedBy() {
    return this.data.receivedBy
      ? store.state.main.users[this.data.receivedBy] || null
      : null;
  }

  /** What the material is: its description, else the order's numbers. */
  toString() {
    return (
      String(this.data.description || "").trim() ||
      this.reference ||
      this.data.vendor ||
      ""
    );
  }
}
